import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { dictionaries, type Language, type TranslationKey } from './translations.js';

/**
 * Language and text direction.
 *
 * Arabic is right-to-left, so switching language is not only a matter of
 * swapping strings — `dir` on <html> flips the whole layout. Every stylesheet
 * rule that could care about side uses logical properties (`inline-start`,
 * `padding-inline`) so the flip needs no separate RTL stylesheet.
 */

type Vars = Record<string, string | number>;

interface LanguageContextValue {
  language: Language;
  direction: 'rtl' | 'ltr';
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
  /** Translate a key, substituting {placeholders}. */
  t: (key: TranslationKey, vars?: Vars) => string;
  /** Locale tag for Intl date and number formatting. */
  locale: string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_KEY = 'mpr.language';

function detectInitialLanguage(): Language {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'ar' || stored === 'en') return stored;
  // Fall back to the browser's preference rather than assuming English.
  return navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectInitialLanguage);

  const direction = language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    // Set on <html> so CSS logical properties and native controls (date
    // pickers, scrollbars, text selection) all follow the same direction.
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language, direction]);

  const setLanguage = useCallback((next: Language) => setLanguageState(next), []);
  const toggleLanguage = useCallback(
    () => setLanguageState((current) => (current === 'ar' ? 'en' : 'ar')),
    [],
  );

  const t = useCallback(
    (key: TranslationKey, vars?: Vars) => {
      // Falls back to English rather than showing a raw key, so a missing
      // Arabic string degrades into readable text instead of debug output.
      const template = dictionaries[language][key] ?? dictionaries.en[key] ?? key;
      if (!vars) return template;

      return Object.entries(vars).reduce(
        (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
        template,
      );
    },
    [language],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      direction,
      setLanguage,
      toggleLanguage,
      t,
      locale: language === 'ar' ? 'ar' : 'en-GB',
    }),
    [language, direction, setLanguage, toggleLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useI18n must be used inside LanguageProvider');
  return context;
}

/** Date formatting that follows the chosen language. */
export function useFormatters() {
  const { locale } = useI18n();

  return useMemo(
    () => ({
      date: (value: string | Date) =>
        new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value)),
      dateTime: (value: string | Date) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(value)),
      time: (value: string | Date) =>
        new Intl.DateTimeFormat(locale, { timeStyle: 'medium' }).format(new Date(value)),
      number: (value: number) => new Intl.NumberFormat(locale).format(value),
    }),
    [locale],
  );
}
